export const ROUTES = {
  // Authentication & Access
  HOME: { path: '/', title: 'Landing Page' },
  SIGNIN: { path: '/signin', title: 'Sign In' },
  FORGOT_PASSWORD: { path: '/forgot-password', title: 'Forgot Password' },
  FORGOT_PASSWORD_CLIENT: { path: '/forgot-password-cliente', title: 'Forgot Password Client' },
  RESET_PASSWORD: { path: '/reset-password', title: 'Reset Password' },
  TERMS_OF_USE: { path: '/termos-de-uso', title: 'Terms of Use' },
  PRIVACY_POLICY: { path: '/politica-de-privacidade', title: 'Privacy Policy' },
  CREATE_EVENT: { path: '/criar-evento', title: 'Create Event' },
  LOGIN_CLIENT_MINUTA: { path: '/login-client-minuta', title: 'Client Login for Minuta' },
  LOGIN_CLIENT: { path: '/login-client', title: 'Client Login' },
  CLIENT_HOME: { path: '/client-home', title: 'Client Home' },
  CEP: { path: '/cep', title: 'Update CEP' },

  // Landing Pages
  MD_MOBILE: { path: '/m', title: 'MD Mobile' },
  KLINGER_MOBILE: { path: '/k', title: 'Klinger Mobile' },
  BAHIA_SUL: { path: '/b', title: 'Bahia Sul' },
  TRISOLUTION: { path: '/t', title: 'Trisolution' },
  DS_PAGE: { path: '/d', title: 'DS Page' },

  // Landing Page Navigation
  CONTACT: { path: '/contato', title: 'Contact' },
  SERVICES: { path: '/servicos', title: 'Services' },
  PREVIOUS: { path: '/previous', title: 'Previous Page' },

  // Dashboard & Authenticated Pages
  DASHBOARD_HOME: { path: '/home', title: 'Dashboard' },
  ALERTS: { path: '/alertas', title: 'Alerts' },
  CLIENTS: { path: '/clientes', title: 'Clients List' },
  GENERATE_MINUTE: { path: '/gerar-minuta', title: 'Generate Minute' },
  
  REGISTER_CLIENT: { path: '/cadastrar-cliente', title: 'Register Client' },
  REGISTER_CLIENT_CNPJ: { path: '/cadastrar-cliente/cnpj', title: 'Register Client CNPJ' },
  REGISTER_CLIENT_PF: { path: '/cadastrar-cliente/pf', title: 'Register Client PF' },
  
  AGREEMENT_SIGNATURES: { path: '/assinatura', title: 'Signatures Management' },
  // Partners
  PARTNERS: { path: '/parceiros', title: 'Partners List' },
  REGISTER_PARTNER: { path: '/parceiros/cadastrar', title: 'Register Partner' },
  REGISTER_ACCOUNTING: { path: '/parceiros/cadastrar/contabilidade', title: 'Register Accounting' },

  // Minute Management
  MINUTE_USER: { path: '/minuta-usuario', title: 'User Minute' },

  // Admin Routes
  ADMIN_HOME: { path: '/admin/home', title: 'Admin Home' },
  ADMIN_USERS: { path: '/admin/usuarios', title: 'Users Management' },
  ADMIN_CONTRACTS: { path: '/admin/contratos', title: 'Contracts Management' },

  ADMIN_PRODUCTS_ANALISTA: { path: '/admin/product-analista', title: 'Products assigned to the analyst' },
  ADMIN_BANK_INFO: { path: '/admin/info-banco', title: 'Bank Information' },

  // Tenant-specific routes (these will have dynamic prefixes)
  FINANCIAL_OPTIONS: { path: '/opcoes-financeiras', title: 'Financial Options' },
  FISCAL_OPTIONS: { path: '/opcoes-fiscais', title: 'Fiscal Options' },

  // Fallback
  NOT_FOUND: { path: '*', title: 'Page Not Found' }
} as const

export const ROUTE_BUILDERS = {
  /**
   * Build minute page route with ID parameter
   */
  minute: (id: string | number) => `/minuta/${id}`,
  /**
   * Build PREVIEW page route with ID parameter
   */
  editPreview: (editId: string | number) => `/edit-preview/${editId}`,
  /**
   * Build minute read route with tenant ID and contracts parameters
   */
  minuteRead: (idTenant: string | number, contratos: string | number) => `/minuta-read/${idTenant}/${contratos}`,

  /**
   * Build user minute route with ID parameter
   */
  minuteUser: (id: string | number) => `/minuta-usuario/${id}`,

  /**
   * Build contract signing route with client ID parameter
   */
  signContract: (idClient: string | number) => `/assinar-contrato/${idClient}`,
  signWitness: (idClient: string | number, slot: string | number) => `/assinar-testemunha/${idClient}/${slot}`,
  /**
   * Build login Client for contract
   */
  loginClientMinuta: (id: string | number) => `/login-client-minuta?id=${id}`,
  /**
   * Build login Client for system
   */
  loginClient: () => `/login-client`,
  clientHomeMinute: (id: string | number) => `/client-home/minuta/${id}`,

  /**
   * Build client dashboard route with CNPJ/CPF parameter
   */
  clientList: (tenant: string) => `/admin/${tenant}/clientes`,

  clientDashboard: (cnpjCpf: string) => `/cliente/${cnpjCpf}`,
  clientDashboardInfo: (cnpjCpf: string) => `/cliente/${cnpjCpf}/informacoes`,
  clientDashboardAddProduct: (cnpjCpf: string) => `/cliente/${cnpjCpf}/adicionar-produto`,
  clientDashboardProducts: (cnpjCpf: string) => `/cliente/${cnpjCpf}/produtos`,

  /**
   * Build client admin dashboard route with CNPJ/CPF parameter
   */
  clientDashboardAdmin: (tenant: string, cnpjCpf: string) => `/admin/${tenant}/clientes/${cnpjCpf}`,

  clientDashboardAdminInfo: (tenant: string, cnpjCpf: string) => `/admin/${tenant}/clientes/${cnpjCpf}/informacoes`,
  clientDashboardAdminAddProduct: (tenant: string, cnpjCpf: string) =>
    `/admin/${tenant}/clientes/${cnpjCpf}/adicionar-produto`,
  clientDashboardAdminProducts: (tenant: string, cnpjCpf: string) => `/admin/${tenant}/clientes/${cnpjCpf}/produtos`,

  /**
   * Build admin client list route with CNPJ/CPF parameter
   */
  adminClientList: (cnpjCpf: string) => `/admin/${cnpjCpf}/clientes`,

  /**
   * Build admin financial options route with CNPJ/CPF parameter
   */
  adminFinancialOptions: (cnpjCpf: string) => `/admin/${cnpjCpf}/opcoes-financeiras`,

  /**
   * Build admin fiscal options route with CNPJ/CPF parameter
   */
  adminFiscalOptions: (cnpjCpf: string) => `/admin/${cnpjCpf}/opcoes-fiscais`
} as const

export type RouteKey = keyof typeof ROUTES
export type RouteBuilderKey = keyof typeof ROUTE_BUILDERS

/**
 * Helper function to get route path by key
 */
export const getRoutePath = (routeKey: RouteKey): string => {
  return ROUTES[routeKey].path
}

/**
 * Helper function to get route title by key
 */
export const getRouteTitle = (routeKey: RouteKey): string => {
  return ROUTES[routeKey].title
}

/**
 * Helper function to build parameterized routes
 */
export const buildRoute = <T extends RouteBuilderKey>(
  builderKey: T,
  ...args: Parameters<(typeof ROUTE_BUILDERS)[T]>
): string => {
  return (ROUTE_BUILDERS[builderKey] as any)(...args)
}
